import { authClient } from "@/auth/auth-client";
import {
  markPendingAccountRegistration,
  trackAccountRegistration,
} from "@/components/analytics/conversions";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FieldError, FieldGroup } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { revalidateLogic, useForm } from "@tanstack/react-form";
import {
  Link,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import z from "zod";

const registerSchema = z
  .object({
    email: z.string().email("Geçerli bir email adresi girin"),
    password: z.string().min(8, "Şifre en az 8 karakter olmalı"),
    confirm: z.string().min(1, "Şifre tekrarı gerekli"),
  })
  .refine((v) => v.password === v.confirm, {
    message: "Şifreler uyuşmuyor",
    path: ["confirm"],
  });

export function RegisterForm() {
  const [serverError, setServerError] = useState<string | null>(null);
  const navigate = useNavigate();
  const router = useRouter();
  const search = useSearch({ strict: false });
  const redirect = (search as any)?.redirect as string | undefined;

  const form = useForm({
    defaultValues: { email: "", password: "", confirm: "" },
    validationLogic: revalidateLogic(),
    validators: { onDynamic: registerSchema },
    onSubmit: async ({ value }) => {
      setServerError(null);
      const { error } = await authClient.signUp.email({
        email: value.email,
        password: value.password,
        name: value.email.split("@")[0],
      });
      if (error) {
        setServerError(error.message || "Kayıt olurken bir hata oluştu.");
      } else {
        trackAccountRegistration("email");
        await router.invalidate();
        await navigate({ to: redirect || "/" });
      }
    },
  });

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted/40 p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex flex-col items-center gap-2">
          <Link to="/">
            <img
              src="/lightLogo.webp"
              alt="Logo"
              className="h-20 w-20 dark:hidden"
            />
            <img
              src="/darkLogo.webp"
              alt="Logo"
              className="hidden h-20 w-20 dark:block"
            />
          </Link>
        </div>
        <Card className="w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Kayıt Ol</CardTitle>
            <CardDescription>
              Hesap oluşturmak için bilgilerini gir
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => {
                markPendingAccountRegistration("google");
                return authClient.signIn.social({
                  provider: "google",
                  callbackURL: redirect || "/",
                });
              }}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google ile Kayıt Ol
            </Button>
            <div className="flex items-center gap-2">
              <Separator className="flex-1" />
              <span className="text-muted-foreground text-xs">
                veya email ile
              </span>
              <Separator className="flex-1" />
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
            >
              <FieldGroup className="gap-4">
                <form.Field name="email">
                  {(field) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="ornek@email.com"
                        autoComplete="email"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </div>
                  )}
                </form.Field>

                <form.Field name="password">
                  {(field) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="password">Şifre</Label>
                      <Input
                        id="password"
                        type="password"
                        autoComplete="new-password"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </div>
                  )}
                </form.Field>

                <form.Field name="confirm">
                  {(field) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="confirm">Şifre Tekrar</Label>
                      <Input
                        id="confirm"
                        type="password"
                        autoComplete="new-password"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </div>
                  )}
                </form.Field>

                {serverError && (
                  <p className="text-destructive text-sm">{serverError}</p>
                )}

                <form.Subscribe selector={(s) => s.isSubmitting}>
                  {(isSubmitting) => (
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Kayıt olunuyor...
                        </>
                      ) : (
                        "Kayıt Ol"
                      )}
                    </Button>
                  )}
                </form.Subscribe>

                <p className="px-2 text-center text-muted-foreground text-xs leading-relaxed">
                  Kayıt olarak{" "}
                  <Link
                    to="/privacy"
                    search={{ tab: "kvkk" }}
                    target="_blank"
                    className="text-foreground underline underline-offset-2 hover:text-primary"
                  >
                    KVKK Aydınlatma Metni
                  </Link>
                  ,{" "}
                  <Link
                    to="/privacy"
                    search={{ tab: "videos" }}
                    target="_blank"
                    className="text-foreground underline underline-offset-2 hover:text-primary"
                  >
                    Video İzinleri
                  </Link>{" "}
                  ve{" "}
                  <Link
                    to="/privacy"
                    search={{ tab: "privacy" }}
                    target="_blank"
                    className="text-foreground underline underline-offset-2 hover:text-primary"
                  >
                    Gizlilik Politikası
                  </Link>
                  'nı kabul etmiş olursunuz.
                </p>

                <p className="text-center text-muted-foreground text-sm">
                  Zaten hesabın var mı?{" "}
                  <Link
                    to="/password/login"
                    search={redirect ? { redirect } : undefined}
                    className="text-foreground underline underline-offset-4 hover:text-primary"
                  >
                    Giriş yap
                  </Link>
                </p>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
